import React, { useState } from 'react';
import {
    Card,
    CardContent,
    Box,
    Typography,
    IconButton,
    Button,
    Stack,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { usePricing } from '../../../hooks/quoter/usePricing';
import { parseSelectionSummary } from '../../../utils/formatSelectionSummary';
import type { QuoteData } from '../../../types/quoter/types';

interface CartItemDetails {
    // Cart-specific fields
    SailName?: string;
    BoatModel?: string;
    AdjustedSquareFeet?: number;
    ClothMakeName?: string;
    ClothDisplayName?: string;
    ClothOverrideGradeId?: number;
    OverrideClothId?: number;
    DefaultClothId?: number;
    IsEverydaySail?: number;
    FullBattens?: number;
    WindAverageAnswerId?: number;
    SailUseAnswerId?: number;
    SailWeightTypeId?: number;
    OptionPrice?: number;
    Notes?: string;
    SailDescription?: string;
    SelectionSummary?: string; // From f_Order_QuoteSelectionSummary

    // Pricing fields from QuoteData
    CalculatedPrice?: number;
    CustomerDiscountPercent?: number;
    CustomerDiscountApplied?: number;
    ResaleDiscountPercent?: number;
    ResaleDiscountApplied?: number;
    ResaleUserPrice?: number;
    QuotedPrice?: number;

    // Additional fields for QuoteData compatibility
    BoatId?: number;
    SailId?: number;
    SailCutId?: number;
    IsFurling?: number;
    PercentLP?: number;
    SailDisplayName?: string;
}

export interface CartItem {
    Order_QuoteId: number;
    OrderId: number;
    details: CartItemDetails;
    options: any[];
    quantity: number;
}

interface CartItemProps {
    item: CartItem;
    index: number;
    isResellerUser: boolean;
    onUpdateQuantity: (index: number, quantity: number) => void;
    onRemove: (item: CartItem) => void;
}

const CartItemComponent: React.FC<CartItemProps> = ({
    item,
    index,
    isResellerUser,
    onUpdateQuantity,
    onRemove
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const pricing = usePricing(item.details as QuoteData);

    // Helper functions
    const isHeadsail = () => {
        const sailName = item.details?.SailName?.toLowerCase() || '';
        const percentLP = item.details?.PercentLP || 0;
        return sailName.includes('headsail') || percentLP > 0;
    };

    const formatLPPercent = () => {
        const percentLP = item.details?.PercentLP;
        if (percentLP && percentLP > 0) {
            return `${percentLP}% LP`;
        }
        return null;
    };

    const getClothName = () => {
        if (item.details?.ClothDisplayName) {
            return item.details.ClothDisplayName;
        }
        return "Standard";
    };

    const formatSailOptions = () => {
        // Use SelectionSummary from database if available (preferred - single source of truth)
        if (item.details?.SelectionSummary) {
            return parseSelectionSummary(item.details.SelectionSummary).map(item => item.text);
        }
        
        // Fallback to manual processing if SelectionSummary not available
        if (!item.options || item.options.length === 0) {
            return [];
        }

        return item.options
            .filter(option => option.ValueSelected > 0)
            .map(option => {
                const cost = option.OptionQuotedPrice !== 0 ? ` (${Math.round(option.OptionQuotedPrice)})` : '';
                const alreadyHasQuantity = option.OptionDetails.includes(': ') ||
                    option.OptionDetails.includes(option.ValueSelected.toString());

                if (option.ValueSelected === 1 || alreadyHasQuantity) {
                    return `${option.OptionDetails}${cost}`;
                } else {
                    return `${option.OptionDetails}: ${option.ValueSelected}${cost}`;
                }
            });
    };

    const d = item.details || {};
    const unitDb = isResellerUser
        ? Number(d.ResaleUserPrice ?? 0)
        : Number(d.CalculatedPrice ?? 0);
    const unit = unitDb > 0 ? unitDb : Number(d.QuotedPrice ?? 0);

    const selectedOptions = formatSailOptions();
    const optionCount = selectedOptions.length;

    // Build summary line
    const summaryParts = [];
    if (isHeadsail() && formatLPPercent()) {
        summaryParts.push(formatLPPercent());
    }
    summaryParts.push(item.details?.BoatModel || 'Boat');
    summaryParts.push(`${item.details?.AdjustedSquareFeet || 0} sq.ft.`);
    if (optionCount > 0) {
        summaryParts.push(`${optionCount} option${optionCount !== 1 ? 's' : ''}`);
    }
    const summaryLine = summaryParts.join(' • ');

    return (
        <Card sx={{ mb: 2 }}>
            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                {/* COLLAPSED VIEW - Header row */}
                <Box
                    onClick={() => setIsExpanded(!isExpanded)}
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: 'action.hover'
                        },
                        borderRadius: 1,
                        p: 1,
                        mx: -1,
                        mb: 1
                    }}
                >
                    <Box sx={{ flex: 1, mr: 2 }}>
                        <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                            {item.details?.SailName || 'Sail'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                            {summaryLine}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                            ${Math.round(pricing?.customerPrice || unit).toLocaleString()}
                        </Typography>
                        <IconButton size="small">
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Box>
                </Box>

                {/* EXPANDED VIEW - Details */}
                {isExpanded && (
                    <Box sx={{ mt: 2 }}>
                        {/* Detailed information */}
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                <strong>Cloth:</strong> {getClothName()}
                            </Typography>

                            {selectedOptions.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Options:</strong>
                                    </Typography>
                                    {selectedOptions.map((option, idx) => (
                                        <Typography
                                            key={idx}
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ pl: 2 }}
                                        >
                                            • {option}
                                        </Typography>
                                    ))}
                                </Box>
                            )}

                            {item.details?.Notes && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    <strong>Notes:</strong> {item.details.Notes}
                                </Typography>
                            )}
                        </Box>

                        {/* Pricing breakdown */}
                        {pricing?.showRetailPrice && (
                            <Box sx={{ mb: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    <strong>Retail Price:</strong> ${Math.round(pricing.retailPrice).toLocaleString()}
                                </Typography>

                                {pricing.showCustomerDiscount && pricing.customerDiscountAmount !== 0 && (
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        <strong>{isResellerUser ? 'Cust. Discount:' : 'Discount:'}</strong>{' '}
                                        {pricing.customerDiscountPercent.toFixed(0)}% ($
                                        {Math.round(pricing.customerDiscountAmount).toLocaleString()})
                                    </Typography>
                                )}

                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {isResellerUser ? '*Cust. Price:' : '*Your Price:'} $
                                    {Math.round(pricing.customerPrice).toLocaleString()}
                                </Typography>

                                {/* Reseller pricing section */}
                                {isResellerUser && (
                                    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                                        {pricing.resellerDiscountAmount !== 0 && (
                                            <Typography variant="body2" color="primary.main" sx={{ mb: 1 }}>
                                                <strong>Your Discount:</strong> {pricing.resellerDiscountPercent.toFixed(0)}% ($
                                                {Math.round(pricing.resellerDiscountAmount).toLocaleString()})
                                            </Typography>
                                        )}
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                            *Your Price: ${Math.round(pricing.resellerPrice).toLocaleString()}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}

                        {/* Quantity Total (if more than 1) */}
                        {item.quantity > 1 && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'right' }}>
                                Total ({item.quantity} items): ${Math.round(unit * item.quantity).toLocaleString()}
                            </Typography>
                        )}
                    </Box>
                )}

                {/* CONTROLS LINE - Quantity controls and Remove button */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onUpdateQuantity(index, item.quantity - 1);
                            }}
                            disabled={item.quantity <= 1}
                        >
                            <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography sx={{ minWidth: '20px', textAlign: 'center' }}>{item.quantity}</Typography>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onUpdateQuantity(index, item.quantity + 1);
                            }}
                        >
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </Stack>

                    <Button
                        startIcon={<DeleteIcon />}
                        color="error"
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemove(item);
                        }}
                        size="small"
                        variant="outlined"
                    >
                        Remove
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
};

export default React.memo(CartItemComponent, (prevProps, nextProps) => {
    // Custom comparison function for better memoization
    return (
        prevProps.item.Order_QuoteId === nextProps.item.Order_QuoteId &&
        prevProps.item.quantity === nextProps.item.quantity &&
        prevProps.index === nextProps.index &&
        prevProps.isResellerUser === nextProps.isResellerUser &&
        JSON.stringify(prevProps.item.details) === JSON.stringify(nextProps.item.details) &&
        JSON.stringify(prevProps.item.options) === JSON.stringify(nextProps.item.options)
    );
});
